import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { getCostAccessState, getCostRates, setCostPassword } from '../../api/client'
import { useCostLock } from './costLock'

/**
 * Ask for the cost password, or set it the first time.
 *
 * The password is verified by actually calling a cost endpoint rather than by
 * a dedicated "check" call: if the rates come back, it was right. One round
 * trip, and no endpoint whose only job is to answer "is this the password",
 * which is the one an attacker would hammer.
 */
export default function CostUnlock({ onClose }) {
  const { t } = useTranslation(['clevel', 'common'])
  const { unlock } = useCostLock()
  const [configured, setConfigured] = useState(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    getCostAccessState()
      .then(data => setConfigured(!!data.configured))
      .catch(() => setConfigured(false))
  }, [])

  const k = (suffix, opts) => t(`capacity.unlock.${suffix}`, opts)

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      if (configured === false) {
        if (password !== confirm) { setError(k('mismatch')); setBusy(false); return }
        await setCostPassword({ new_password: password })
      } else {
        await getCostRates(password)
      }
      unlock(password)
      onClose?.()
    } catch (err) {
      setError(err.response?.data?.detail || k('failed'))
    } finally {
      setBusy(false)
    }
  }

  const first = configured === false

  return (
    <Modal title={first ? k('titleFirst') : k('title')} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(420px, 80vw)' }}>
        <p className="text-sm text-gray-600">{first ? k('introFirst') : k('intro')}</p>

        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {first ? k('newPassword') : k('password')}
          </label>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !first) submit() }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {first && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {k('confirmPassword')}
            </label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit() }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        )}

        {/* Said plainly, because somebody will otherwise assume it is encryption. */}
        <p className="text-[11px] text-gray-400 leading-relaxed">{k('caveat')}</p>

        <div className="flex gap-3">
          <button
            onClick={submit}
            disabled={busy || !password}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
          >
            {busy ? k('checking') : first ? k('setIt') : k('open')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
